// @ts-nocheck
import type { ValidationIssue } from '../../diagnostics';
import type { AnyScriptDefinition, ComputedValueDefinition } from '../../types/canonical';
import { NullHostBridge } from '../host/NullHostBridge';
import { executeRuntimeScript } from '../scripts/RuntimeScriptExecutor';
import type { BindingContext, BindingResolutionResult } from './types';
import { resolveActiveLocale } from '../host/resolveActiveLocale';

const mountComputedCache = new Map<string, unknown>();

function mountCacheKey(itemId: number | string, elementId: string, computedId: string): string {
  return `${itemId}::${elementId}::${computedId}`;
}

export function clearMountComputedCacheForItem(itemId: number | string, elementId?: string): void {
  const prefix = elementId ? `${itemId}::${elementId}::` : `${itemId}::`;
  for (const key of mountComputedCache.keys()) {
    if (key.startsWith(prefix)) {
      mountComputedCache.delete(key);
    }
  }
}

function coerceComputedValue(value: unknown, outputType: string): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (outputType === 'boolean') {
    return Boolean(value);
  }
  if (outputType === 'number') {
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  }
  if (outputType === 'string') {
    return String(value);
  }
  return value;
}

function notFound(computedId: string, message: string, severity: ValidationIssue['severity'] = 'info'): BindingResolutionResult {
  return {
    found: false,
    source: 'computed',
    diagnostics: [{
      severity,
      code: 'semantic.missing_property',
      message,
      path: computedId,
    }],
  };
}

export async function evaluateComputedDefinition(
  definition: ComputedValueDefinition,
  context: BindingContext,
): Promise<BindingResolutionResult> {
  if (definition.type === 'expression') {
    return evaluateExpressionComputed(definition, context);
  }

  if (definition.type === 'script') {
    const scriptId = definition.script?.scriptId;
    if (!scriptId) {
      return notFound(definition.id, `Computed '${definition.id}' script is missing scriptId`, 'error');
    }
    const script = context.scriptsById?.get(scriptId);
    if (!script) {
      return notFound(definition.id, `Computed '${definition.id}' references unknown script '${scriptId}'`, 'error');
    }

    const capabilities = context.capabilities ?? new Set<string>(['read.item']);
    const hostBridge = context.hostBridge ?? NullHostBridge;

    if (context.dispatchComputedScript) {
      const value = await context.dispatchComputedScript(scriptId, definition.script?.args);
      if (value === undefined) {
        return notFound(definition.id, `Computed script '${scriptId}' returned no value`);
      }
      return {
        found: true,
        value: coerceComputedValue(value, definition.outputType),
        source: 'computed',
      };
    }

    const scriptResult = await executeRuntimeScript(script, {
      bindingContext: context,
      hostBridge,
      capabilities,
      cancellationToken: { isCancelled: false },
      runtimeMode: 'render',
      declaredCapabilities: new Set(script.permissions.map((p) => p.capability)),
      locale: resolveActiveLocale(),
    });

    if (!scriptResult.ok) {
      return {
        found: false,
        source: 'computed',
        diagnostics: scriptResult.issues,
      };
    }

    if (scriptResult.returnValue === undefined) {
      return notFound(definition.id, `Computed script '${scriptId}' did not return a value`);
    }

    return {
      found: true,
      value: coerceComputedValue(scriptResult.returnValue, definition.outputType),
      source: 'computed',
      diagnostics: scriptResult.issues.length > 0 ? scriptResult.issues : undefined,
    };
  }

  return notFound(definition.id, `Computed '${definition.id}' has unsupported type`, 'error');
}

function evaluateExpressionComputed(
  definition: ComputedValueDefinition,
  context: BindingContext,
): BindingResolutionResult {
  if (!definition.expression?.id) {
    return notFound(definition.id, `Computed '${definition.id}' expression is missing expression.id`, 'error');
  }
  if (!context.expressionEngine) {
    return notFound(definition.id, `Computed '${definition.id}' requires an expression engine`);
  }
  const result = context.expressionEngine.evaluate(
    definition.expression.id,
    context,
    {
      profile: 'production',
      expectedType: definition.outputType as 'boolean' | 'number' | 'string' | 'null' | undefined,
    },
  );
  if (!result.ok || result.value === null) {
    return { found: false, source: 'computed', diagnostics: result.issues };
  }
  return {
    found: true,
    value: coerceComputedValue(result.value, definition.outputType),
    source: 'computed',
    diagnostics: result.issues.length > 0 ? result.issues : undefined,
  };
}

export function evaluateComputedDefinitionSync(
  definition: ComputedValueDefinition,
  context: BindingContext,
): BindingResolutionResult {
  if (definition.type !== 'expression') {
    return notFound(
      definition.id,
      `Computed '${definition.id}' (${definition.type}) requires async hydration before resolve`,
    );
  }
  return evaluateExpressionComputed(definition, context);
}

export async function hydrateComputedResults(context: BindingContext): Promise<Record<string, unknown>> {
  const results: Record<string, unknown> = { ...(context.computedResults ?? {}) };
  const definitions = context.computedValuesById;
  if (!definitions) {
    return results;
  }

  const itemId = context.item.id;
  const elementId = context.elementId ?? '';

  for (const [computedId, definition] of Object.entries(definitions)) {
    if (results[computedId] !== undefined) {
      continue;
    }

    const cachePolicy = definition.cachePolicy ?? 'none';
    if (cachePolicy === 'mount' && elementId) {
      const cacheKey = mountCacheKey(itemId, elementId, computedId);
      if (mountComputedCache.has(cacheKey)) {
        results[computedId] = mountComputedCache.get(cacheKey);
        continue;
      }
    }

    const resolved = await evaluateComputedDefinition(definition, context);
    if (resolved.found && resolved.value !== undefined) {
      results[computedId] = resolved.value;
      if (cachePolicy === 'mount' && elementId) {
        mountComputedCache.set(mountCacheKey(itemId, elementId, computedId), resolved.value);
      }
    }
  }

  return results;
}

export function hydrateExpressionComputedResultsSync(context: BindingContext): Record<string, unknown> {
  const results: Record<string, unknown> = { ...(context.computedResults ?? {}) };
  const definitions = context.computedValuesById;
  if (!definitions || !context.expressionEngine) {
    return results;
  }

  const itemId = context.item.id;
  const elementId = context.elementId ?? '';

  for (const [computedId, definition] of Object.entries(definitions)) {
    if (results[computedId] !== undefined || definition.type !== 'expression') {
      continue;
    }

    const cachePolicy = definition.cachePolicy ?? 'none';
    if (cachePolicy === 'mount' && elementId) {
      const cacheKey = mountCacheKey(itemId, elementId, computedId);
      if (mountComputedCache.has(cacheKey)) {
        results[computedId] = mountComputedCache.get(cacheKey);
        continue;
      }
    }

    const resolved = evaluateComputedDefinitionSync(definition, context);
    if (resolved.found && resolved.value !== undefined) {
      results[computedId] = resolved.value;
      if (cachePolicy === 'mount' && elementId) {
        mountComputedCache.set(mountCacheKey(itemId, elementId, computedId), resolved.value);
      }
    }
  }

  return results;
}

export function resolveComputedRef(
  computedId: string,
  context: BindingContext,
): BindingResolutionResult {
  if (context.computedResults && context.computedResults[computedId] !== undefined) {
    return { found: true, value: context.computedResults[computedId], source: 'computed' };
  }

  const definition = context.computedValuesById?.[computedId];
  if (!definition) {
    return notFound(computedId, `Unknown computed value '${computedId}'`);
  }

  if (definition.type === 'expression') {
    return evaluateComputedDefinitionSync(definition, context);
  }

  return notFound(
    computedId,
    `Computed '${computedId}' (script) is not hydrated — call hydrateComputedResults first`,
  );
}

export function buildComputedValuesById(
  definitions: readonly ComputedValueDefinition[] | undefined,
): Record<string, ComputedValueDefinition> {
  const map: Record<string, ComputedValueDefinition> = {};
  for (const def of definitions ?? []) {
    map[def.id] = def;
  }
  return map;
}

export function buildScriptsById(
  scripts: readonly AnyScriptDefinition[] | undefined,
): ReadonlyMap<string, AnyScriptDefinition> {
  return new Map((scripts ?? []).map((script) => [script.id, script]));
}
